import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { differenceInDays } from 'date-fns';
import { isEmpty } from 'lodash-es';

import defaultLogo from '../../../assets/images/CompanyDefaultLogo.svg';
import boltIcon from '../../../assets/images/icon-bolt.svg';
import map from '../../../assets/images/map-pin.svg';
import tableIconUserShare from '../../../assets/images/tabler-icon-user-share.svg';
import ButtonWithIcon from '../../../components/shared/ButtonWithIcon/ButtonWithIcon';
import ExperientialRewardsPill from '../../../components/shared/ExperientialRewardsPill/ExperientialRewardsPill';
import LabelWithIcon from '../../../components/shared/Label/LabelWithIcon';
import MonetaryRewardsPill from '../../../components/shared/MonetaryRewardsPill/MonetaryRewardsPill';
import SmartRewardPill from '../../../components/shared/SmartRewardPill/SmartRewardPill';
import {
  getDateTimeFromNow,
  isDateValid,
  locationObjToStr,
  normalizeURL,
  renderSalaryRange,
} from '../../../helpers/commonHelper';
import { IsMobile } from '../../../helpers/IsMobile';
import { useIsIOS } from '../../../hooks/useDeviceDetection';
import useReferralProviderData from '../../../hooks/useReferralProviderData';
import { saveCandidateInfo } from '../../../redux/actions/referRequestActions';
import urls from '../../../routing/urls';
import { selectCurrentUser } from '../../../selectors/authSelectors';
import { selectCurrentOrganization } from '../../../selectors/organizationSelectors';
import CandidateInfoModal from '../CandidateInfoModal/CandidateInfoModal';
import DepartmentIcon from '../Shared/DepartmentIcon/DepartmentIcon';

import JobInfoSection from './JobInfoSection';

import styles from './JobSection.module.scss';

const JobSection = ({ job, shouldShowButtons = true, fromBrowse = false, isPublic = false }) => {
  const { t } = useTranslation('common');
  const {
    category,
    logo_image_url,
    title,
    published_date,
    organization_name,
    job_location,
    min_salary,
    max_salary,
    work_place_type,
    work_type,
    job_experience_name,
    job_urgency_type_name,
    rewards,
    candidate_status,
    reward_visibility,
  } = job;

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { monetary, currency_names, experiential } = rewards || {};
  const date = getDateTimeFromNow(published_date);
  const publishedDays = differenceInDays(new Date(), new Date(published_date));
  const salaryRange = renderSalaryRange(min_salary, max_salary, job.currency_id);
  const isMobile = IsMobile();
  const isIOSDevice = useIsIOS();
  const dispatch = useDispatch();

  const user = useSelector(selectCurrentUser);
  const organization = useSelector(selectCurrentOrganization);

  const [showModal, setShowModal] = useState(false);

  const isCandidateApplied = candidate_status?.applied;
  const { referralProviderData } = useReferralProviderData();

  const navigateToJob = (jobId) => {
    localStorage.setItem('activeTab', 'browse');
    navigate(`${urls.jobDetail.replace(':jobId', jobId)}`);
  };

  const redirectToOrg = (e) => {
    e.stopPropagation();

    if (organization?.general_setting_identifier) {
      const orgIdentifier = organization?.general_setting_identifier;
      navigate(isPublic ? `/company/${orgIdentifier}` : `/jobs/company/${orgIdentifier}`);
    }
  };

  const handleApplyJob = (e) => {
    e.stopPropagation();

    if (job?.external_job_link) {
      if (user) {
        const data = {
          firstName: user.first_name,
          lastName: user.last_name,
          phone_number: user.phone_number,
          email: user.email,
          job_id: job.id,
          full_name: `${user.first_name} ${user.last_name}`,
          from_id: referralProviderData?.user?.id ? referralProviderData?.user?.id : user.id,
        };
        dispatch(
          saveCandidateInfo(data, () => {
            if (job?.external_job_link) {
              if (isIOSDevice) {
                const link = job.external_job_link;
                const url = normalizeURL(link);
                window.location.href = url;
              } else {
                const link = job.external_job_link;
                const url = normalizeURL(link);
                window.open(url, '_blank');
              }
            }
          })
        );
        return;
      }
      setShowModal(true);
      return;
    }

    const url = pathname.includes('company') ? urls.companyApplicationFlow : urls.applicationFlow;
    navigate(`${url.replace(':companyName', organization?.general_setting_identifier)}?apply_for=${job.uuid}`);
  };

  const renderRewardPills = () => {
    if (reward_visibility && reward_visibility.visibility !== 'none') {
      return (
        <div className={styles.jobRewardTemplate}>
          <SmartRewardPill rewardVisibility={reward_visibility} />
        </div>
      );
    }

    if (!reward_visibility) {
      return (
        <>
          {!!monetary && (
            <div className={styles.jobRewardTemplate}>
              <MonetaryRewardsPill monetaryReward={monetary} currencyId={currency_names} useCommas />
            </div>
          )}
          {!isEmpty(experiential) && <ExperientialRewardsPill />}
        </>
      );
    }

    return null;
  };

  return (
    <div className={shouldShowButtons ? styles.content : 'w-full p-0'}>
      <div className={styles.companyInfo}>
        <div
          className={styles.logo}
          onClick={redirectToOrg}
          role="link"
          tabIndex={0}
          title={organization_name}
          onKeyDown={(e) => e.key === 'Enter' && redirectToOrg()}
        >
          <span>
            {category === 'department' ? (
              <div title={t('companyDepartment')}>
                <img src={logo_image_url || defaultLogo} alt="profile-bg" className={styles.logo} />
                <DepartmentIcon departmentContainer={styles.departmentIconDiv} />
              </div>
            ) : (
              <img src={logo_image_url || defaultLogo} alt="profile-bg" className={styles.logo} />
            )}
          </span>
        </div>

        <div className={styles.jobDescriptionColumn}>
          <div className={styles.jobDetailsColumn}>
            <div className={styles.jobAndCompanyNameColumn}>
              <div className={styles.jobNameRow}>
                <div className={styles.jobHeading}>
                  <button
                    type="button"
                    className={classNames(styles.jobName, fromBrowse && styles.jobNameDisabled)}
                    onClick={() => (fromBrowse ? null : navigateToJob(job?.uuid))}
                    onKeyDown={(e) => e.key === 'Enter' && (fromBrowse ? null : navigateToJob(job?.uuid))}
                    disabled={fromBrowse}
                    title={title}
                    role="link"
                    tabIndex={0}
                  >
                    {title}
                  </button>
                  {publishedDays < 3 && (
                    <LabelWithIcon varient="label-new" tag={t('labels.new')} customStyles={styles.newLabelTop} />
                  )}
                </div>
                <div className={styles.topRewardsRow}>
                  {renderRewardPills()}
                </div>
              </div>
              <div
                title={organization_name}
                onKeyDown={(e) => e.key === 'Enter' && redirectToOrg()}
                className={styles.companyName}
                onClick={redirectToOrg}
                role="link"
                tabIndex={0}
              >
                {organization_name}
              </div>
            </div>
            {(!isEmpty(job_location) || !isEmpty(salaryRange)) && (
              <div className={styles.locationAndSalaryRow}>
                {!isEmpty(job_location) && (
                  <div className={styles.locationRow}>
                    <img src={map} className={styles.mapIcon} alt="map" />
                    <span className={styles.locationAndSalaryRangeText}>{locationObjToStr(job_location)}</span>
                  </div>
                )}

                {!isEmpty(salaryRange) && !isMobile ? <div className={styles.lineBreak} /> : null}

                {!isEmpty(salaryRange) && !isMobile ? (
                  <div className={`${styles.locationAndSalaryRangeText}`}>{salaryRange}</div>
                ) : null}
              </div>
            )}
            <JobInfoSection
              work_place_type={work_place_type}
              work_type={work_type}
              job_experience_name={job_experience_name}
              job_urgency_type_name={job_urgency_type_name}
              customStyles={styles.jobInfoSection}
            />
          </div>
          <div className={styles.jobDateRow}>
            <div className={styles.jobDateText}>{isDateValid(published_date) && date}</div>
            {publishedDays < 3 && (
              <LabelWithIcon varient="label-new" tag={t('labels.new')} customStyles={styles.newLabelBottom} />
            )}
          </div>
          {shouldShowButtons && (
            <div className={styles.btnRow}>
              <ButtonWithIcon
                disabled={isCandidateApplied}
                text={isCandidateApplied ? t('buttons.applied') : t('buttons.apply')}
                img={boltIcon}
                imgClassName="btn-icon-36"
                textClassName="btn-icon-text-primary-40"
                variant="secondary-36"
                onClick={handleApplyJob}
                isExternalLink={job?.external_job_link}
              />
              <ButtonWithIcon
                text={t('buttons.refer')}
                img={tableIconUserShare}
                imgClassName="btn-icon-36"
                textClassName="btn-icon-text-tertiary-40"
                variant="tertiary-36"
                onClick={(e) => {
                  e.stopPropagation();

                  const url = pathname.includes('company') ? urls.companyReferral : urls.refer;
                  navigate(
                    `${url.replace(':companyName', organization?.general_setting_identifier)}?refer_for=${job.uuid}`
                  );
                }}
              />
            </div>
          )}

          {showModal && (
            <CandidateInfoModal
              showModal={showModal}
              setShowModal={setShowModal}
              organization={organization_name}
              jobTitle={title}
              job={job}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default JobSection;
